import React, { useState, useEffect } from "react";
import { Container, Row, Col, Image, Card, Form, Button } from "react-bootstrap";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import IconProgress from "../../images/logo-lg.png";
import IconEmail from '../../images/profile/icon_email.png';
import IconPhone from '../../images/profile/icon_phone.png';
import IconCompany from '../../images/profile/icon_company.png';
import IconAddress from '../../images/profile/icon_address.png';
import IocnCamera from '../../images/icon-camera.png';
import { BiEditAlt } from "react-icons/bi";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import apiConfig from "../credentials/apiConfig"; // Make sure path is correct
import './index.scss';

function Profile() {
  const navigate = useNavigate();
  const [user, setUser] = useState({
    firstName: '',
    lastName: '',
    companyName: '',
    profileImageUrl: null,
    email: '',
    title: '',
    phoneNumber: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zipCode: ''
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Fetch user data when component mounts
    const fetchUserData = async () => {
      const userId = localStorage.getItem("userId");

      // Check if user is already authenticated
      if (userId) {
        try {
          // Fetch user profile data from API
          const response = await axios.get(
            `${apiConfig.apiBaseURL}/getUserProfile/${userId}`,
            {
              headers: {
                "x-api-key": apiConfig["x-api-key"],
                "Content-Type": "application/json"
              }
            }
          );

          const data = response.data;
          if (data && data.success && data.user) {
            //console.log("API Response data:", data.user); // For debugging

            // Update user state with data from API
            setUser({
              firstName: data.user.firstName || '',
              lastName: data.user.lastName || '',
              companyName: data.user.companyName || '',
              profileImageUrl: data.user.profileImageUrl || null,
              email: data.user.email || '',
              title: data.user.title || '',
              phoneNumber: data.user.phoneNumber || '',
              address1: data.user.address1 || '',
              address2: data.user.address2 || '',
              city: data.user.city || '',
              state: data.user.state || '',
              zipCode: data.user.zipCode || ''
            });
          } else {
            console.error("Failed to load profile data from API");
            fallbackToLocalStorage();
          }
        } catch (error) {
          console.error("Error fetching user profile from API:", error);
          fallbackToLocalStorage();
        } finally {
          setIsLoading(false);
        }
      } else {
        setIsLoading(false);
      }
    };

    const fallbackToLocalStorage = () => {
      // Try to use cached data from localStorage as fallback
      const userProfile = localStorage.getItem("userProfile");
      if (userProfile) {
        try {
          const parsedProfile = JSON.parse(userProfile);
          setUser({
            firstName: parsedProfile.firstName || '',
            lastName: parsedProfile.lastName || '',
            companyName: parsedProfile.companyName || '',
            profileImageUrl: parsedProfile.profileImageUrl || null,
            email: parsedProfile.email || '',
            title: parsedProfile.title || '',
            phoneNumber: parsedProfile.phoneNumber || '',
            address1: parsedProfile.address1 || '',
            address2: parsedProfile.address2 || '',
            city: parsedProfile.city || '',
            state: parsedProfile.state || '',
            zipCode: parsedProfile.zipCode || ''
          });
        } catch (error) {
          console.error("Error parsing user profile:", error);
        }
      }
    };

    fetchUserData();
  }, []);

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const fileSize = file.size / 1024 / 1024;
      const fileType = file.type;
      if ((fileType === "image/jpeg" || fileType === "image/png") && fileSize <= 15) {
        const reader = new FileReader();
        reader.onloadend = () => {
          // Update the user state with the new image
          setUser({
            ...user,
            profileImageUrl: reader.result
          });

          toast.success("Profile photo updated successfully!", {
            position: "top-right",
            autoClose: 5000,
          });

          // In a real application, you would also need to upload this image to your server
          // and update the user profile in the database
        };
        reader.readAsDataURL(file);
      } else {
        toast.error("Only .jpg, .jpeg, or .png files under 15MB are allowed.", {
          position: "top-right",
          autoClose: 5000,
        });
      }
    }
  };

  const handleEditProfile = () => {
    navigate("/edit-profile");
  };
  const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return 'Phone not available';

    // Remove all non-numeric characters
    const numericValue = phoneNumber.replace(/\D/g, '');

    // Check if we have enough digits
    if (numericValue.length < 10) return phoneNumber; // Return original if not enough digits

    // Format as (XXX) XXX-XXXX
    return `(${numericValue.slice(0, 3)}) ${numericValue.slice(3, 6)}-${numericValue.slice(6, 10)}`;
  };

  // Display full name if available, otherwise show placeholder
  const fullName = user.firstName && user.lastName
    ? `${user.firstName} ${user.lastName}`
    : "User Name";

  // Format the complete address
  const formatAddress = () => {
    const parts = [];
    if (user.address1) parts.push(user.address1);


    const cityStateZip = [];
    if (user.city) cityStateZip.push(user.city);
    if (user.state) cityStateZip.push(user.state);
    if (user.zipCode) cityStateZip.push(user.zipCode);

    if (cityStateZip.length > 0) {
      parts.push(cityStateZip.join(', '));
    }

    return parts.join(', ') || "Address not available";
  };

  // Get initials for profile image placeholder
  const getInitials = () => {
    if (user.firstName && user.lastName) {
      return `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`;
    }
    return "";
  };

  return (
    <Container className="my-5 profile">
      <Row className="justify-content-center">
        <h5 className="fw-bolder text-center mb-5">Profile</h5>
        <Col md={12} lg={8} className="mt-5">
          <Card className="shadow border-0 p-4 text-center position-relative" style={{ backgroundColor: '#E7EFF8' }}>

            <div
              className="profile-image-container text-center mb-5 position-absolute"
              style={{
                width: "150px",
                height: "150px",
                top: "-20%",
                left: "50%",
                transform: "translateX(-50%)",
              }}
            >
              {!isLoading && user.profileImageUrl ? (
                <Image
                  src={user.profileImageUrl}
                  roundedCircle
                  width={150}
                  height={150}
                  className="profile-image"
                  style={{
                    border: "3px solid #FFFFFF",
                    objectFit: "cover",
                    width: "150px",
                    height: "150px",
                  }}
                />
              ) : (
                <div
                  className="d-flex align-items-center justify-content-center bg-secondary text-white rounded-circle"
                  style={{
                    width: "150px",
                    height: "150px",
                    border: "3px solid #FFFFFF",
                    fontSize: "3rem",
                  }}
                >
                  {getInitials()}
                </div>
              )}
            </div>

            <div className="mt-3">
              <Card.Title className="mt-5 mb-3 text-dark fw-bold" style={{ fontSize: '30px' }}>
                {fullName}
              </Card.Title>
            </div>

            <Card.Subtitle className="mb-3 text-light">{user.title || "Position"}</Card.Subtitle>
            <Row className="text-start mt-4">
              <Col md={6} className="mb-5 d-flex">
                <div className="me-3 my-auto">
                  <img src={IconEmail} alt="email" />
                </div>
                <div>
                  <strong className="text-primary">Email</strong>
                  <p className="text-light mb-0">{user.email || "email@example.com"}</p>
                </div>
              </Col>
              <Col md={6} className="mb-5 d-flex">
                <div className="me-3 my-auto">
                  <img src={IconPhone} alt="phone" />
                </div>
                <div>
                  <strong className="text-primary">Phone</strong>
                  <p className="text-light mb-0">{formatPhoneNumber(user.phoneNumber)}</p>
                </div>
              </Col>
              <Col md={6} className="mb-5 d-flex">
                <div className="me-3 my-auto">
                  <img src={IconCompany} alt="company" />
                </div>
                <div>
                  <strong className="text-primary">Company Name</strong>
                  <p className="text-light mb-0">{user.companyName || "Company"}</p>
                </div>
              </Col>
              <Col md={6} className="mb-5 d-flex">
                <div className="me-3 my-auto">
                  <img src={IconAddress} alt="address" />
                </div>
                <div>
                  <strong className="text-primary">Address</strong>
                  <p className="text-light mb-0">{formatAddress()}</p>
                </div>
              </Col>
            </Row>
            <Button variant="primary" className="position-absolute bg-primary px-4" style={{ top: -50, right: 20, cursor: "pointer" }} onClick={handleEditProfile}><BiEditAlt color="#fff" size={20} className="me-2" />Edit Profile</Button>
          </Card>
        </Col>
      </Row>
      {/* <ToastContainer /> */}
    </Container>
  );
}

export default Profile;